import isEqual from 'lodash/isEqual';
import {weakMemo} from '@packages/utilities/memo';
import {Component} from '@packages/systems/wfdl';
import {parsePropId} from '@packages/systems/code-libraries/propTypes';
import {
  extractComponentStateScenarios,
  MAX_COMPONENT_STATE_SCENARIOS,
  type ComponentStateScenario,
  type ExtractComponentStateScenariosOptions,
} from '@packages/systems/code-libraries/component-states';
import {
  COMPONENT_PREVIEW_SCENARIOS_DEFINED,
  COMPONENT_PREVIEW_SCENARIO_CREATED,
  COMPONENT_PREVIEW_SCENARIO_EDITED,
  COMPONENT_PREVIEW_SCENARIO_REMOVED,
} from './actionTypes';
import type {
  componentPreviewScenariosDefined,
  componentPreviewScenarioCreated,
  componentPreviewScenarioEdited,
  componentPreviewScenarioRemoved,
} from './actionCreators';

export type ComponentPreviewScenarioAction = ReturnType<
  | typeof componentPreviewScenariosDefined
  | typeof componentPreviewScenarioCreated
  | typeof componentPreviewScenarioEdited
  | typeof componentPreviewScenarioRemoved
>;

type ComponentType = Component.ComponentType;

/**
 * Names of a component's Variant props, as the code island receives them
 * (variant group ids are prop ids like `w-prop--variant--Variant`). State
 * props must never set these: variants are the grid's other axis.
 */
export function getVariantPropNames(
  variants: Component.Variants | undefined
): ReadonlySet<string> {
  return new Set(Object.keys(variants ?? {}).map(parsePropId));
}

/**
 * Whether a re-normalized list still holds `scenarioId` and every state the
 * stored list keeps once normalized. Comparing with the normalized list, not
 * the stored one, lets an edit through when stored states already collapse
 * (they differed only by a Variant prop the store now strips).
 */
function keepsScenarios(
  nextScenarios: readonly ComponentStateScenario[],
  existingScenarios: readonly ComponentStateScenario[],
  options: ExtractComponentStateScenariosOptions,
  scenarioId: string
): boolean {
  const nextIds = new Set(nextScenarios.map(({id}) => id));
  return (
    nextIds.has(scenarioId) &&
    extractComponentStateScenarios(existingScenarios, options).every(({id}) =>
      nextIds.has(id)
    )
  );
}

// The reducer and analytics share the accepted result for this
// action/component, including rejected manual edits. Neither consumer repeats
// normalization.
export const getNextComponentPreviewScenarios = weakMemo(
  (action: ComponentPreviewScenarioAction) =>
    weakMemo((component: ComponentType): ComponentStateScenario[] | null => {
      const source = Component.getSource(component);
      if (!source) return null;
      const existingScenarios = source.meta?.states ?? [];
      const options = {
        excludePropKeys: getVariantPropNames(Component.getVariants(component)),
      };
      switch (action.type) {
        case COMPONENT_PREVIEW_SCENARIOS_DEFINED:
          return extractComponentStateScenarios(
            action.payload.scenarios,
            options
          );
        case COMPONENT_PREVIEW_SCENARIO_REMOVED: {
          const nextScenarios = existingScenarios.filter(
            (scenario) => scenario.id !== action.payload.scenarioId
          );
          // No-op when the scenario wasn't recorded on this component.
          return nextScenarios.length === existingScenarios.length
            ? null
            : nextScenarios;
        }
        case COMPONENT_PREVIEW_SCENARIO_EDITED: {
          const {scenarioId, label, props} = action.payload;
          // No-op when the scenario wasn't recorded on this component.
          if (
            !existingScenarios.some((scenario) => scenario.id === scenarioId)
          ) {
            return null;
          }

          // Re-normalize the whole list (trim, drop blanks, freeze props) so an
          // edit can't store a malformed scenario, mirroring the "defined" path.
          const nextScenarios = extractComponentStateScenarios(
            existingScenarios.map((scenario) =>
              scenario.id === scenarioId
                ? {id: scenario.id, label, ...(props ? {props} : {})}
                : scenario
            ),
            options
          );
          // Guard against normalization dropping the edited scenario (e.g. a blank
          // label slipping through) collapsing the whole set.
          return keepsScenarios(
            nextScenarios,
            existingScenarios,
            options,
            scenarioId
          )
            ? nextScenarios
            : null;
        }
        case COMPONENT_PREVIEW_SCENARIO_CREATED: {
          const {scenarioId, label, props} = action.payload;
          // Defense in depth against the UI's plus control being bypassed: never
          // exceed the per-component cap, and never collide with an existing id.
          if (existingScenarios.length >= MAX_COMPONENT_STATE_SCENARIOS) {
            return null;
          }
          if (
            existingScenarios.some((scenario) => scenario.id === scenarioId)
          ) {
            return null;
          }

          // Re-normalize the whole list (trim, drop blanks, freeze props) so a
          // creation can't store a malformed scenario, mirroring the other paths.
          const nextScenarios = extractComponentStateScenarios(
            [
              ...existingScenarios,
              {id: scenarioId, label, ...(props ? {props} : {})},
            ],
            options
          );
          // Guard against normalization dropping the new scenario (e.g. a blank
          // label slipping through), which would silently no-op the creation.
          return keepsScenarios(
            nextScenarios,
            existingScenarios,
            options,
            scenarioId
          )
            ? nextScenarios
            : null;
        }
      }
    })
);

export function summarizeComponentStateChanges(
  before: readonly ComponentStateScenario[],
  after: readonly ComponentStateScenario[]
) {
  const previousById = new Map(
    before.map((scenario) => [scenario.id, scenario])
  );
  const nextIds = new Set(after.map((scenario) => scenario.id));
  const addedStateIds: string[] = [];
  const modifiedStates: {
    id: string;
    changed_fields: ('label' | 'props')[];
  }[] = [];
  for (const scenario of after) {
    const previous = previousById.get(scenario.id);
    if (!previous) {
      addedStateIds.push(scenario.id);
      continue;
    }
    const changedFields: ('label' | 'props')[] = [];
    if (previous.label !== scenario.label) changedFields.push('label');
    if (!isEqual(previous.props, scenario.props)) changedFields.push('props');
    if (changedFields.length) {
      modifiedStates.push({id: scenario.id, changed_fields: changedFields});
    }
  }
  const removedStateIds = before
    .filter(({id}) => !nextIds.has(id))
    .map(({id}) => id);
  const orderChanged = !isEqual(
    before.filter(({id}) => nextIds.has(id)).map(({id}) => id),
    after.filter(({id}) => previousById.has(id)).map(({id}) => id)
  );
  if (
    !addedStateIds.length &&
    !removedStateIds.length &&
    !modifiedStates.length &&
    !orderChanged
  ) {
    return null;
  }
  return {
    state_count_before: before.length,
    state_count_after: after.length,
    added_state_ids: addedStateIds,
    removed_state_ids: removedStateIds,
    modified_states: modifiedStates,
    order_changed: orderChanged,
  };
}

/**
 * Whether the reducer would apply this action. An edit it rejects (one that
 * would leave two states with identical props) is dropped as a no-op, so a
 * caller that removes a draft on the strength of the write checks this first.
 */
export function isComponentPreviewScenarioActionAccepted(
  action: ComponentPreviewScenarioAction,
  component: ComponentType | undefined
): boolean {
  return (
    component !== undefined &&
    getNextComponentPreviewScenarios(action)(component) !== null
  );
}
